import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Typography,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import { getId } from "../../utils/idHelpers";

const statusConfig = {
  processing: { label: "Processing", color: "info" },
  ready_for_review: { label: "Ready for Review", color: "warning" },
  approved: { label: "Approved", color: "success" },
  completed: { label: "Completed", color: "success" },
  error: { label: "Error", color: "error" },
};

const LocationImportBatches = ({ open, onClose, onSelectBatch }) => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (open) {
      loadBatches();
      // Refresh batches every 3 seconds to show status updates
      const interval = setInterval(loadBatches, 3000);
      return () => clearInterval(interval);
    }
  }, [open]);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "LocationImportBatch",
        action: "list",
        filter: {},
        pageNumber: 0,
        recordsPerPage: 100,
      });

      if (results?.isSuccessful && Array.isArray(results.data?.records)) {
        setBatches(results.data.records);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("[LocationImportBatches] Error loading batches:", error);
      showMessage("Failed to load batches", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBatch = async (batch) => {
    const batchId = batch?.batchId;
    if (!batchId) return;

    setDeleting(batchId);
    try {
      await executeAction({
        entityName: "LocationImportBatch",
        action: "delete",
        data: { batchId },
      });

      showMessage("Batch deleted successfully");
      await loadBatches();
    } catch (error) {
      showMessage(`Failed to delete batch: ${error.message}`, "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleSelectBatch = (batch) => {
    if (batch.status === "ready_for_review") {
      onSelectBatch(batch);
      onClose();
    } else {
      showMessage(
        `Batch must be in "Ready for Review" status to proceed`,
        "warning",
      );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Location Import Batches</DialogTitle>
      <DialogContent>
        {loading && !batches.length ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
          >
            <CircularProgress />
          </Box>
        ) : batches.length === 0 ? (
          <Typography
            color="textSecondary"
            align="center"
            sx={{ py: 3 }}
          >
            No import batches yet. Create one using the Import button.
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ mt: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Batch Name</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => {
                  const config = statusConfig[batch.status] || {
                    label: batch.status,
                    color: "default",
                  };
                  const batchKey = getId(batch) || batch.batchId;
                  return (
                    <TableRow key={batchKey}>
                      <TableCell>
                        <Stack>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                          >
                            {batch.batchName}
                          </Typography>
                          {batch.notes && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                            >
                              {batch.notes}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{batch.recordCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={config.label}
                          color={config.color}
                          size="small"
                          icon={
                            batch.status === "ready_for_review" ? (
                              <PendingIcon />
                            ) : (
                              <CheckCircleIcon />
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(batch.createdAt)}</TableCell>
                      <TableCell align="center">
                        {batch.status === "ready_for_review" && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSelectBatch(batch)}
                            sx={{ mr: 1 }}
                          >
                            Review
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDelete(batch)}
                          disabled={deleting === batch.batchId}
                        >
                          {deleting === batch.batchId ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Dialog_YesNo
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete batch?"
        message={`Delete "${confirmDelete?.batchName || "this batch"}" and all its staging records?`}
        onYes={async () => {
          const batch = confirmDelete;
          setConfirmDelete(null);
          await handleDeleteBatch(batch);
        }}
        onNo={() => setConfirmDelete(null)}
      />
    </Dialog>
  );
};

export default LocationImportBatches;
